import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './LicenseAcceptancePage.module.css';

export default function LicenseAcceptancePage({
  currentLicense,
  status,
  onAccept,
  loading = false,
}) {
  const { t } = useTranslation();
  const [acceptedLicense, setAcceptedLicense] = useState(false);
  const [acceptedCommercialTerms, setAcceptedCommercialTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const summary = useMemo(() => (
    Array.isArray(currentLicense?.summary) && currentLicense.summary.length
      ? currentLicense.summary
      : [
          t('Smarti is proprietary software.'),
          t('Commercial or internal company use requires a separate written commercial license agreement.'),
        ]
  ), [currentLicense, t]);

  const canSubmit = acceptedLicense && acceptedCommercialTerms && !submitting && !loading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await onAccept?.({
        licenseVersion: currentLicense?.licenseVersion || status?.licenseVersion,
        acceptedLicense: true,
        acceptedCommercialTerms: true,
      });
    } catch (submitError) {
      setError(submitError.message || t('Could not store license acceptance.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <p className={styles.eyebrow}>{t('Smarti Access Required')}</p>
        <h1 className={styles.title}>{t('Smarti Software License Agreement')}</h1>
        <p className={styles.subtitle}>
          {t('Before using the application, you must accept the current Smarti Software License Agreement and commercial-use terms.')}
        </p>

        <div className={styles.grid}>
          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>{t('Key points before you continue')}</h2>
            <ul className={styles.summaryList}>
              {summary.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </section>

          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>{t('Full license text')}</h2>
            <div className={styles.licenseBodyWrap}>
              <div className={styles.licenseBody}>
                {currentLicense?.licenseText || t('Loading current license text...')}
              </div>
            </div>
          </section>
        </div>

        <section className={`${styles.panel} ${styles.termsPanel}`}>
          <h2 className={styles.panelTitle}>{t('Commercial terms and pricing summary')}</h2>
          <div className={styles.licenseBodyWrap}>
            <div className={styles.licenseBody}>
              {currentLicense?.commercialTermsText || t('Loading commercial terms...')}
            </div>
          </div>
        </section>

        <div className={styles.checks}>
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={acceptedLicense}
              onChange={(event) => setAcceptedLicense(event.target.checked)}
            />
            <span className={styles.checkLabel}>
              {t('I have read and accept the Smarti Software License Agreement.')}
            </span>
          </label>

          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={acceptedCommercialTerms}
              onChange={(event) => setAcceptedCommercialTerms(event.target.checked)}
            />
            <span className={styles.checkLabel}>
              {t('I understand that commercial or internal company use requires a separate written commercial license agreement and may be subject to the commercial terms.')}
            </span>
          </label>
        </div>

        {error ? <div className={styles.error}>{error}</div> : null}

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.button}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {submitting ? t('Accepting...') : t('Accept and Continue')}
          </button>
        </div>
      </div>
    </div>
  );
}
